import { useCallback } from "react";
import { useIntl } from "react-intl";
import { DataTableBooleanColumn } from "~components/DataTable/components/DataTableBooleanColumn/DataTableBooleanColumn";
import { DataTableDateColumn } from "~components/DataTable/components/DataTableDateColumn/DataTableDateColumn.tsx";
import type { DataTableColumn } from "~components/DataTable/types/dataTableColumn.ts";
import { dateTimeFormat } from "~constants/dateFormat.ts";
import { CompetitorTypes } from "~enums/competitorTypes.ts";
import { useSportConfiguration } from "~hooks/useSportConfiguration.ts";
import {
  EntityColumn,
  FixtureNameColumn,
  FixtureNumberColumn,
  PersonsColumn,
  PoolColumn,
  RoundNumberColumn,
  ScoreColumn,
  SeriesColumn,
  StageColumn,
  StartDateColumn,
  StartTimeColumn,
  StatusColumn,
  TypeColumn,
  VenueColumn,
} from "~pages/Fixtures/FixtureListPage/components";
import { FixtureTableColumns } from "~pages/Fixtures/FixtureListPage/types/fixtureColumnsEnum.ts";
import { isFixtureEditable } from "~pages/Fixtures/FixtureListPage/utilities";
import { useFixtureService } from "~pages/Fixtures/hooks";
import type { FixtureTransformed } from "~pages/Fixtures/types";
import { sortStringValues } from "~utils/utilities.ts";

export const useGetFixtureTableColumns = (massEditMode: boolean): Array<DataTableColumn<FixtureTransformed>> => {
  const { formatMessage } = useIntl();
  const { updateFixture, isUpdatePending } = useFixtureService();
  const listUpdateProps = { updateFixture, isUpdatePending };

  const sportConfiguration = useSportConfiguration();

  const isEditable = useCallback(
    (fixture: FixtureTransformed, checkStatus = true) => isFixtureEditable(fixture, checkStatus) && massEditMode,
    [massEditMode],
  );

  const homeCompetitor = {
    align: "end",
    title: formatMessage({ id: "fixtureListPage.table.column.homeCompetitor" }),
    key: FixtureTableColumns.HomeCompetitor,
    width: 165,
    sorter: (a: FixtureTransformed, b: FixtureTransformed) =>
      sortStringValues(a.homeCompetitor?.entity?.nameFullLocal, b.homeCompetitor?.entity?.nameFullLocal),
    render: (_: string, fixture: FixtureTransformed) => {
      return (
        <EntityColumn
          editMode={isEditable(fixture)}
          fixtureId={fixture.fixtureId}
          entity={fixture.homeCompetitor?.entity}
          isHome
          oppositeEntity={fixture.awayCompetitor?.entity}
          {...listUpdateProps}
        />
      );
    },
  };

  const homeParticipants = {
    align: "end",
    title: formatMessage({
      id: "fixtureListPage.table.column.homeParticipants",
    }),
    key: FixtureTableColumns.HomeParticipants,
    width: 165,
    sorter: (a: FixtureTransformed, b: FixtureTransformed) =>
      sortStringValues(
        a.homeParticipants?.map((x: any) => x.person?.nameFullLocal).join(",") ?? "",
        b.homeParticipants?.map((x: any) => x.person?.nameFullLocal).join(",") ?? "",
      ),
    render: (_: string, fixture: FixtureTransformed) => {
      return (
        <PersonsColumn
          editMode={isEditable(fixture)}
          fixtureId={fixture.fixtureId}
          persons={fixture.homeParticipants?.map((x: any) => x.person)}
          isHome
          oppositePersons={fixture.awayParticipants?.map((x: any) => x.person)}
          {...listUpdateProps}
        />
      );
    },
  };

  const awayCompetitor = {
    title: formatMessage({ id: "fixtureListPage.table.column.awayCompetitor" }),
    key: FixtureTableColumns.AwayCompetitor,
    width: 165,
    sorter: (a: FixtureTransformed, b: FixtureTransformed) =>
      sortStringValues(a.awayCompetitor?.entity?.nameFullLocal, b.awayCompetitor?.entity?.nameFullLocal),
    render: (_: string, fixture: FixtureTransformed) => {
      return (
        <EntityColumn
          editMode={isEditable(fixture)}
          fixtureId={fixture.fixtureId}
          entity={fixture.awayCompetitor?.entity}
          isHome={false}
          oppositeEntity={fixture.homeCompetitor?.entity}
          {...listUpdateProps}
        />
      );
    },
  };

  const awayParticipants = {
    title: formatMessage({
      id: "fixtureListPage.table.column.awayParticipants",
    }),
    key: FixtureTableColumns.AwayParticipants,
    width: 165,
    sorter: (a: FixtureTransformed, b: FixtureTransformed) =>
      sortStringValues(
        a.awayParticipants?.map((x: any) => x.person?.nameFullLocal).join(",") ?? "",
        b.awayParticipants?.map((x: any) => x.person?.nameFullLocal).join(",") ?? "",
      ),
    render: (_: string, fixture: FixtureTransformed) => {
      return (
        <PersonsColumn
          editMode={isEditable(fixture)}
          fixtureId={fixture.fixtureId}
          persons={fixture.awayParticipants?.map((x: any) => x.person)}
          isHome={false}
          oppositePersons={fixture.homeParticipants?.map((x: any) => x.person)}
          {...listUpdateProps}
        />
      );
    },
  };

  let away = {};
  let home = {};

  if (sportConfiguration.competitorType === CompetitorTypes.Entity) {
    away = awayCompetitor;
    home = homeCompetitor;
  } else if (sportConfiguration.competitorType === CompetitorTypes.Person) {
    away = awayParticipants;
    home = homeParticipants;
  } else {
    throw new Error(`Unsupported competitor type: ${sportConfiguration.competitorType}`);
  }

  const columns = [
    {
      title: formatMessage({ id: "fixtureListPage.table.column.fixtureId" }),
      dataIndex: "fixtureId",
      key: FixtureTableColumns.FixtureId,
      copyable: true,
      width: 200,
      align: "center",
    },
    {
      title: formatMessage({ id: "fixtureListPage.table.column.externalId" }),
      dataIndex: "externalId",
      key: FixtureTableColumns.ExternalId,
      copyable: true,
      align: "center",
      width: 150,
    },
    {
      title: formatMessage({ id: "fixtureListPage.table.column.roundNumber" }),
      dataIndex: "roundNumber",
      key: FixtureTableColumns.RoundNumber,
      width: 90,
      align: "center",
      sorter: (a: FixtureTransformed, b: FixtureTransformed) => sortStringValues(a.roundNumber ?? "", b.roundNumber ?? ""),
      render: (value: string, fixture: FixtureTransformed) => {
        return <RoundNumberColumn editMode={isEditable(fixture)} fixtureId={fixture.fixtureId} value={value} {...listUpdateProps} />;
      },
    },
    home,
    {
      title: formatMessage({ id: "fixtureListPage.table.column.result" }),
      dataIndex: "result",
      key: FixtureTableColumns.Result,
      align: "center",
      width: 110,
      render: (_, fixture: FixtureTransformed) => {
        return <ScoreColumn editMode={isEditable(fixture)} fixtureId={fixture.fixtureId} value={fixture} {...listUpdateProps} />;
      },
    },
    away,
    {
      title: formatMessage({
        id: "fixtureListPage.table.column.fixtureNumber",
      }),
      dataIndex: "fixtureNumber",
      key: FixtureTableColumns.FixtureNumber,
      align: "center",
      width: 80,
      sorter: (a: FixtureTransformed, b: FixtureTransformed) => (a.fixtureNumber ?? 0) - (b.fixtureNumber ?? 0),
      render: (value: string, fixture: FixtureTransformed) => {
        return (
          <FixtureNumberColumn editMode={isEditable(fixture, false)} fixtureId={fixture.fixtureId} value={value} {...listUpdateProps} />
        );
      },
    },
    {
      title: formatMessage({ id: "fixtureListPage.table.column.name" }),
      dataIndex: "nameLocal",
      key: FixtureTableColumns.NameLocal,
      align: "center",
      width: 100,
      sorter: (a: FixtureTransformed, b: FixtureTransformed) => sortStringValues(a.nameLocal ?? "", b.nameLocal ?? ""),
      render: (value: string, fixture: FixtureTransformed) => {
        return <FixtureNameColumn editMode={isEditable(fixture, false)} fixtureId={fixture.fixtureId} value={value} {...listUpdateProps} />;
      },
    },
    {
      title: formatMessage({ id: "fixtureListPage.table.column.nameLatin" }),
      dataIndex: "nameLatin",
      key: FixtureTableColumns.NameLatin,
      align: "center",
      width: 100,
      sorter: (a: FixtureTransformed, b: FixtureTransformed) => sortStringValues(a.nameLatin ?? "", b.nameLatin ?? ""),
      render: (value: string, fixture: FixtureTransformed) => {
        return <FixtureNameColumn editMode={isEditable(fixture, false)} fixtureId={fixture.fixtureId} value={value} {...listUpdateProps} />;
      },
    },
    {
      title: formatMessage({ id: "fixtureListPage.table.column.venue" }),
      dataIndex: "venueId",
      key: FixtureTableColumns.VenueId,
      width: 135,
      sorter: (a: FixtureTransformed, b: FixtureTransformed) => sortStringValues(a.venue?.nameLocal, b.venue?.nameLocal),
      render: (_: string, fixture: FixtureTransformed) => {
        return <VenueColumn editMode={isEditable(fixture)} fixtureId={fixture.fixtureId} venue={fixture.venue} {...listUpdateProps} />;
      },
    },
    {
      title: formatMessage({ id: "fixtureListPage.table.column.date" }),
      dataIndex: "startDate",
      key: FixtureTableColumns.StartDate,
      align: "center",
      width: 110,
      sorter: (a: FixtureTransformed, b: FixtureTransformed) => new Date(a.startDate ?? 0).getTime() - new Date(b.startDate ?? 0).getTime(),
      render: (value: string, fixture: FixtureTransformed) => {
        return <StartDateColumn editMode={isEditable(fixture)} fixtureId={fixture.fixtureId} value={value} {...listUpdateProps} />;
      },
    },
    {
      title: formatMessage({ id: "fixtureListPage.table.column.startTime" }),
      dataIndex: "startTime",
      key: FixtureTableColumns.StartTime,
      align: "center",
      width: 80,
      sorter: (a, b) => sortStringValues(a.startTime?.split("T")?.[1], b.startTime?.split("T")?.[1]),
      render: (value: string, fixture: FixtureTransformed) => {
        return (
          <StartTimeColumn
            editMode={isEditable(fixture)}
            fixtureId={fixture.fixtureId}
            timesUnconfirmed={fixture.timesUnconfirmed}
            value={value}
            {...listUpdateProps}
          />
        );
      },
    },
    {
      title: formatMessage({ id: "fixtureListPage.table.column.type" }),
      dataIndex: "fixtureType",
      key: FixtureTableColumns.FixtureType,
      align: "center",
      width: 100,
      sorter: (a, b) => sortStringValues(a.fixtureType, b.fixtureType),
      render: (value: string, fixture: FixtureTransformed) => {
        return <TypeColumn editMode={isEditable(fixture, false)} fixtureId={fixture.fixtureId} value={value} {...listUpdateProps} />;
      },
    },
    {
      title: formatMessage({ id: "fixtureListPage.table.column.status" }),
      dataIndex: "status",
      key: FixtureTableColumns.Status,
      align: "center",
      width: 100,
      sorter: (a, b) => sortStringValues(a.status, b.status),
      render: (value: string, fixture: FixtureTransformed) => {
        return <StatusColumn editMode={isEditable(fixture, false)} fixtureId={fixture.fixtureId} value={value} {...listUpdateProps} />;
      },
    },
    {
      title: formatMessage({ id: "fixtureListPage.table.column.stage" }),
      dataIndex: "stageCode",
      key: FixtureTableColumns.Stage,
      align: "center",
      width: 100,
      sorter: (a, b) => sortStringValues(a.stage?.nameLocal, b.stage?.nameLocal),
      render: (_: string, fixture: FixtureTransformed) => {
        return <StageColumn editMode={isEditable(fixture)} fixtureId={fixture.fixtureId} stage={fixture.stage} {...listUpdateProps} />;
      },
    },
    {
      title: formatMessage({ id: "fixtureListPage.table.column.stageCode" }),
      dataIndex: "stageCode",
      key: FixtureTableColumns.StageCode,
      copyable: true,
      align: "center",
      width: 100,
    },
    {
      title: formatMessage({ id: "fixtureListPage.table.column.pool" }),
      dataIndex: "poolCode",
      key: FixtureTableColumns.Pool,
      align: "center",
      width: 100,
      sorter: (a, b) => sortStringValues(a.pool?.nameLocal, b.pool?.nameLocal),
      render: (_: string, fixture: FixtureTransformed) => {
        return <PoolColumn editMode={isEditable(fixture)} fixtureId={fixture.fixtureId} pool={fixture.pool} {...listUpdateProps} />;
      },
    },
    {
      title: formatMessage({ id: "fixtureListPage.table.column.poolCode" }),
      dataIndex: "poolCode",
      key: FixtureTableColumns.PoolCode,
      copyable: true,
      align: "center",
      width: 100,
    },
    {
      title: formatMessage({ id: "fixtureListPage.table.column.series" }),
      dataIndex: "seriesCode",
      key: FixtureTableColumns.Series,
      align: "center",
      width: 100,
      sorter: (a, b) => sortStringValues(a.series?.nameLocal, b.series?.nameLocal),
      render: (_: string, fixture: FixtureTransformed) => {
        return <SeriesColumn editMode={isEditable(fixture, false)} fixtureId={fixture.fixtureId} fixture={fixture} {...listUpdateProps} />;
      },
    },
    {
      title: formatMessage({ id: "fixtureListPage.table.column.seriesCode" }),
      dataIndex: "seriesCode",
      key: FixtureTableColumns.SeriesCode,
      copyable: true,
      align: "center",
      width: 100,
    },
    {
      title: formatMessage({ id: "fixtureListPage.table.column.added" }),
      dataIndex: "added",
      key: FixtureTableColumns.Added,
      align: "center",
      width: 120,
      render: (value: string) => <DataTableDateColumn value={value} showTime format={dateTimeFormat} />,
    },
    {
      title: formatMessage({ id: "fixtureListPage.table.column.attendance" }),
      dataIndex: "attendance",
      key: FixtureTableColumns.Attendance,
      align: "center",
      width: 70,
    },
    {
      title: formatMessage({ id: "fixtureListPage.table.column.discipline" }),
      dataIndex: "discipline",
      key: FixtureTableColumns.Discipline,
      align: "center",
      width: 100,
    },
    {
      title: formatMessage({
        id: "fixtureListPage.table.column.startTimeActualUtc",
      }),
      dataIndex: "startTimeActualUtc",
      key: FixtureTableColumns.StartTimeActualUtc,
      align: "center",
      width: 140,
      render: (value: string) => <DataTableDateColumn value={value} showTime format={dateTimeFormat} />,
    },
    {
      title: formatMessage({
        id: "fixtureListPage.table.column.endTimeActualUtc",
      }),
      dataIndex: FixtureTableColumns.EndTimeActualUtc,
      key: "endTimeActualUtc",
      align: "center",
      width: 120,
      render: (value: string) => <DataTableDateColumn value={value} showTime format={dateTimeFormat} />,
    },
    {
      title: formatMessage({
        id: "fixtureListPage.table.column.fixtureProfile",
      }),
      dataIndex: "fixtureProfile",
      key: FixtureTableColumns.FixtureProfile,
      align: "center",
      width: 100,
      render: (_, fixture: FixtureTransformed) => fixture?.fixtureProfile?.name,
    },
    {
      title: formatMessage({ id: "fixtureListPage.table.column.profileId" }),
      dataIndex: "profileId",
      key: FixtureTableColumns.ProfileId,
      copyable: true,
      align: "center",
      width: 200,
    },
    {
      title: formatMessage({
        id: "fixtureListPage.table.column.internationalReference",
      }),
      dataIndex: "internationalReference",
      key: FixtureTableColumns.InternationalReference,
      align: "center",
      width: 140,
    },
    {
      title: formatMessage({ id: "fixtureListPage.table.column.round" }),
      dataIndex: "round",
      key: FixtureTableColumns.Round,
      width: 120,
      align: "center",
      render: (_, fixture: FixtureTransformed) => fixture.round?.nameLocal,
    },
    {
      title: formatMessage({ id: "fixtureListPage.table.column.roundCode" }),
      dataIndex: "roundCode",
      key: FixtureTableColumns.RoundCode,
      copyable: true,
      align: "center",
      width: 100,
    },
    {
      title: formatMessage({
        id: "fixtureListPage.table.column.seriesFixtureNumber",
      }),
      dataIndex: "seriesFixtureNumber",
      key: FixtureTableColumns.SeriesFixtureNumber,
      align: "center",
      width: 140,
    },
    {
      title: formatMessage({
        id: "fixtureListPage.table.column.timesUnconfirmed",
      }),
      dataIndex: "timesUnconfirmed",
      key: FixtureTableColumns.TimesUnconfirmed,
      align: "center",
      width: 140,
      render: (value: boolean | null) => {
        return <DataTableBooleanColumn value={value} />;
      },
    },
    {
      title: formatMessage({ id: "fixtureListPage.table.column.startTimeUtc" }),
      dataIndex: "startTimeUtc",
      key: FixtureTableColumns.StartTimeUtc,
      align: "center",
      width: 150,
      render: (value: string) => <DataTableDateColumn value={value} showTime format={dateTimeFormat} />,
    },
  ];

  return columns.map((item: any) => ({
    ...item,
  }));
};
